import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu, X, LogOut, Package, ChevronDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useShop } from "../context/ShopContext";
import Footer from "./Footer";

function ScrollToHash() {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const frame = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(frame);
  }, [hash, pathname]);

  return null;
}

export default function Layout() {
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const { salesEnabled } = useShop();
  const { cart } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate("/");
  };

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/#servizi", label: "Servizi" },
    { to: "/#punti-vendita", label: "Sedi" },
    ...(salesEnabled ? [{ to: "/prodotti", label: "Pneumatici" }] : []),
  ];

  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  const prefetchProducts = () => {
    if (!salesEnabled || !apiUrl) return;
    const url = `${apiUrl}/api/products?page=1&limit=20&sort=newest`;
    fetch(url, { method: "GET" }).catch(() => {});
  };

  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToHash />
      <header className="bg-pg-dark/95 backdrop-blur-xl text-white sticky top-0 z-50 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center shrink-0">
              <img
                src="/images/pg-logo.webp"
                alt="Puntogomme Lazio"
                width={200}
                height={40}
                fetchPriority="high"
                className="h-8 sm:h-10 w-auto"
              />
            </Link>

            <nav className="hidden md:flex items-center gap-0.5">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onMouseEnter={link.to === "/prodotti" ? prefetchProducts : undefined}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                    location.pathname === link.to
                      ? "bg-white/12 text-white font-semibold"
                      : "text-gray-400 hover:text-white hover:bg-white/8"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-1.5">
              {salesEnabled && (
              <Link
                to="/carrello"
                className="relative p-2.5 rounded-xl hover:bg-white/10 transition-all duration-150"
                aria-label={cart.count > 0 ? `Carrello (${cart.count} articoli)` : "Carrello"}
              >
                <ShoppingCart size={20} />
                {cart.count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-pg-red text-pg-dark text-xs w-5 h-5 rounded-full flex items-center justify-center font-black shadow-sm">
                    {cart.count}
                  </span>
                )}
              </Link>
              )}

              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/10 transition-all duration-150 text-sm"
                  >
                    <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center">
                      <User size={15} />
                    </div>
                    <span className="hidden md:block font-medium">{user?.first_name}</span>
                    <ChevronDown size={13} className="text-gray-400" />
                  </button>
                  {userMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                      <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 py-2 animate-fade-in overflow-hidden">
                        <div className="px-4 py-2 border-b border-gray-100 mb-1">
                          <p className="text-xs text-gray-400 font-medium">Account</p>
                          <p className="text-sm font-semibold text-gray-900 truncate">{user?.email}</p>
                        </div>
                        <Link
                          to="/profilo"
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <User size={15} /> Profilo
                        </Link>
                        <Link
                          to="/ordini"
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <Package size={15} /> I miei ordini
                        </Link>
                        {isAdmin && (
                          <Link
                            to="/admin"
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-pg-red font-medium hover:bg-amber-50 transition-colors"
                            onClick={() => setUserMenuOpen(false)}
                          >
                            <Menu size={15} /> Pannello Admin
                          </Link>
                        )}
                        <div className="border-t border-gray-100 mt-1 pt-1">
                          <button
                            onClick={handleLogout}
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 w-full transition-colors"
                          >
                            <LogOut size={15} /> Esci
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <Link to="/login" className="btn-primary btn-sm text-sm">
                  Accedi
                </Link>
              )}

              <button
                className="md:hidden p-2.5 rounded-xl hover:bg-white/10 transition-all"
                aria-label={mobileOpen ? "Chiudi menu" : "Apri menu"}
                aria-expanded={mobileOpen}
                onClick={() => setMobileOpen(!mobileOpen)}
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>

          {mobileOpen && (
            <div className="md:hidden pb-3 border-t border-white/8 pt-2 animate-fade-in space-y-0.5">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="block px-4 py-2.5 rounded-xl text-sm text-gray-300 hover:text-white hover:bg-white/8 transition-all"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className="flex-1 min-h-[calc(100vh-4rem)]">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
}
